import { supabase } from "@/integrations/supabase/client";

export interface UserProfileData {
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string | null;
}

export interface ProfileUpdateResult {
  success: boolean;
  error?: string;
  data?: UserProfileData;
}

/**
 * Service for managing user profile operations
 */
export const profileService = {
  /**
   * Get current user profile data
   */
  async getUserProfile(userId: string): Promise<{
    success: boolean;
    data?: UserProfileData;
    error?: string;
  }> {
    try {
      console.log("Loading user profile:", userId);

      // Get current authenticated user for email (since email is not in profiles table)
      const { data: userData, error: userError } =
        await supabase.auth.getUser();
      if (userError) throw userError;

      // Get profile data for the specified userId
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("first_name, last_name, avatar_url")
        .eq("id", userId)
        .single();

      if (profileError && profileError.code !== "PGRST116") {
        // PGRST116 = no rows returned
        throw profileError;
      }

      // Only include email if this is the current user's profile
      const isCurrentUser = userData.user?.id === userId;

      const userProfileData: UserProfileData = {
        firstName: profileData?.first_name || "",
        lastName: profileData?.last_name || "",
        email: isCurrentUser ? userData.user?.email || "" : "", // Only show email for current user
        avatarUrl: profileData?.avatar_url || null,
      };

      return {
        success: true,
        data: userProfileData,
      };
    } catch (error) {
      console.error("Error loading user profile:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  /**
   * Update user profile data (firstName, lastName)
   */
  async updateUserProfile(
    userId: string,
    updates: { firstName: string; lastName: string },
  ): Promise<ProfileUpdateResult> {
    try {
      console.log("Updating user profile:", { userId, updates });

      const { data, error } = await supabase.from("profiles").upsert(
        {
          id: userId,
          first_name: updates.firstName.trim(),
          last_name: updates.lastName.trim(),
          full_name:
            `${updates.firstName.trim()} ${updates.lastName.trim()}`.trim(),
        },
        { onConflict: "id" },
      );

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error updating user profile:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to update profile",
      };
    }
  },

  /**
   * Fetch minimal profile info for a list of user IDs
   */
  async getProfilesByIds(userIds: string[]): Promise<{
    success: boolean;
    data?: Array<{
      id: string;
      firstName?: string;
      lastName?: string;
      fullName?: string;
    }>;
    error?: string;
  }> {
    try {
      if (!userIds || userIds.length === 0) return { success: true, data: [] };
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, full_name")
        .in("id", userIds);

      if (error) throw error;

      const mapped = (data || []).map((p: any) => ({
        id: p.id as string,
        firstName: p.first_name as string | undefined,
        lastName: p.last_name as string | undefined,
        fullName: p.full_name as string | undefined,
      }));

      return { success: true, data: mapped };
    } catch (error) {
      console.error("Error fetching profiles by IDs:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  /**
   * Upload and update user avatar
   */
  async uploadAvatar(userId: string, file: File): Promise<ProfileUpdateResult> {
    try {
      console.log("Uploading user avatar:", { userId, fileName: file.name });

      // Validate file
      if (!file.type.startsWith("image/")) {
        throw new Error("Please upload an image file (JPG, PNG, etc.)");
      }

      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        throw new Error("File size exceeds 5MB limit.");
      }

      // Generate unique file path
      const fileExt = file.name.split(".").pop();
      const fileName = `${userId}_avatar_${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload file to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("image_library")
        .upload(`user_assets/${filePath}`, file, {
          upsert: true,
          contentType: file.type,
        });

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw new Error(`Error uploading avatar: ${uploadError.message}`);
      }

      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from("image_library")
        .getPublicUrl(`user_assets/${filePath}`);

      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded avatar");
      }

      const avatarUrl = publicUrlData.publicUrl;

      // Update profile with new avatar URL
      const { error: updateError } = await supabase.from("profiles").upsert(
        {
          id: userId,
          avatar_url: avatarUrl,
        },
        { onConflict: "id" },
      );

      if (updateError) {
        console.error("Profile update error:", updateError);
        throw new Error(`Error updating profile: ${updateError.message}`);
      }

      return {
        success: true,
      };
    } catch (error) {
      console.error("Avatar upload error:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to upload avatar",
      };
    }
  },

  /**
   * Remove user avatar
   */
  async removeAvatar(userId: string): Promise<ProfileUpdateResult> {
    try {
      console.log("Removing user avatar:", userId);

      const { error } = await supabase.from("profiles").upsert(
        {
          id: userId,
          avatar_url: null,
        },
        { onConflict: "id" },
      );

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error removing avatar:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to remove avatar",
      };
    }
  },

  /**
   * Validate profile data
   */
  validateProfileData(data: { firstName: string; lastName: string }): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!data.firstName.trim()) {
      errors.push("First name is required");
    }

    if (!data.lastName.trim()) {
      errors.push("Last name is required");
    }

    if (data.firstName.trim().length > 100) {
      errors.push("First name must be less than 100 characters");
    }

    if (data.lastName.trim().length > 100) {
      errors.push("Last name must be less than 100 characters");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
};
